/**
 * Order items only store variation ids, and the backend returns them sorted by id (= creation order),
 * not in the order the product's modification groups are arranged in the backoffice. The kitchen wants
 * them in the product's order, so we re-sort on the frontend using the product's modification groups.
 *
 * Both `Item.modificationGroups` and `ModificationGroup.variations` are saved in the backoffice's
 * drag-and-drop order, so the array indexes are the sort order.
 */

interface GroupWithVariations {
  variations: ReadonlyArray<{ id: string }>;
}

/** variationId -> [groupIndex, indexInGroup] */
export type VariationOrderMap = Map<string, [number, number]>;

/**
 * Builds the sort key for every variation in the product's groups.
 * If a variation exists in several groups, the first group (in product order) wins.
 */
export const buildVariationOrderMap = (
  groups: ReadonlyArray<GroupWithVariations>,
): VariationOrderMap => {
  const map: VariationOrderMap = new Map();

  groups.forEach((group, groupIndex) => {
    (group.variations || []).forEach((variation, indexInGroup) => {
      if (!map.has(variation.id)) {
        map.set(variation.id, [groupIndex, indexInGroup]);
      }
    });
  });

  return map;
};

/**
 * Sorts variation ids in the product's group order (and in-group order).
 * - Ids not found in any group (group missing from cache, variation removed from the group, ...) keep
 *   their original relative order and are placed after the known ones, so nothing is ever dropped.
 * - Duplicates are kept.
 * - Returns a new array; the input is not mutated.
 */
export const sortVariationIdsByProductOrder = (
  variationIds: ReadonlyArray<string>,
  orderMap: VariationOrderMap,
): string[] => {
  // Nothing to sort by - keep the incoming order
  if (orderMap.size === 0) {
    return [...variationIds];
  }

  return variationIds
    .map((id, originalIndex) => ({ id, originalIndex, key: orderMap.get(id) }))
    .sort((a, b) => {
      if (a.key && b.key) {
        return a.key[0] - b.key[0] || a.key[1] - b.key[1] || a.originalIndex - b.originalIndex;
      }
      if (a.key) return -1; // Known before unknown
      if (b.key) return 1;
      return a.originalIndex - b.originalIndex; // Unknowns keep their incoming order
    })
    .map((entry) => entry.id);
};
